import React from 'react'
import { useTranslation } from 'react-i18next'

import Table from '@mui/material/Table'
import TableContainer from '@mui/material/TableContainer'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import Stack from '@mui/material/Stack'
import EditIcon from '@mui/icons-material/Edit'
import CloseIcon from '@mui/icons-material/Close'
import Divider from '@mui/material/Divider'
import Tooltip from '@mui/material/Tooltip'

import { Log } from '../../types/log'
import { getLogName } from '../../utils/logUtils'
import { toReadableTimestamp, calculateTimeDiff } from '../../utils/dateUtils'
import { getDisplayTemperature } from '../../utils/graphUtils'
import ProfileLink from './profileLink'
import InventoryLink from './inventoryLink'
import InfoElement from '../fields/infoElement'
import TooltipIconButton from '../fields/tooltipIconButton'
import { Machine } from '../../types/machine'
import { Datapoint } from '../../types/datapoint'
import { calculateWeightLoss, weightValueToDisplay } from '../../utils/weightUtils'

interface LogInfoTableConfigValue {
  label: string
  /*
  Intention was for pen icon to only be 
  visable when hoovering over info element, 
  but became a regression for touch screen.
  Will be fixed in the future
  */
  hovering_label?: JSX.Element
  renderer: (x: Log) => string | JSX.Element | undefined
}

interface LogInfoTableProps {
  logs: Array<Log>
  logDatapoints: { [logUrl: string]: Array<Datapoint> }
  machine: Machine | null
  selectedLogId?: number
  onSelectedLog?: (logId: number) => void
  onDeselectLog?: (logId: number) => void
  onSetInventory?: () => void
  onSetBatchWeight?: () => void
  onSetColor?: () => void
  onSetName?: () => void
  children?: JSX.Element | Array<JSX.Element>
}

const LogInfoTable = (props: LogInfoTableProps) => {
  const { t } = useTranslation()

  const tableRowConfig: Array<LogInfoTableConfigValue> = [
    {
      label: t('logview.logname'),
      hovering_label: (
        <Stack direction="row" justifyContent="space-between">
          <Stack sx={{ py: 1 }}>{t('logview.logname')}</Stack>
          {props.onSetName && (
            <TooltipIconButton
              onClick={props.onSetName}
              tooltip={t('logview.editlogname')}
              icon={<EditIcon fontSize="small" />}
            />
          )}
        </Stack>
      ),
      renderer: (x: Log) => {
        return (
          <Stack direction="row" alignItems="flex-start">
            {getLogName(x, '', t('logview.batch'))}
            <Divider />
          </Stack>
        )
      },
    },
    {
      label: t('logview.date'),
      renderer: (x: Log) => toReadableTimestamp(x.start_timestamp),
    },
    {
      label: t('logview.profile'),
      renderer: (x: Log) => {
        return <ProfileLink machineSlug={props.machine?.slug || ''} log={x} />
      },
    },
    {
      label: t('logview.weight'),
      hovering_label: (
        <Stack direction="row" justifyContent="space-between">
          <Stack sx={{ py: 1 }}> {t('logview.weight')}</Stack>
          {props.onSetBatchWeight && (
            <TooltipIconButton
              onClick={props.onSetBatchWeight}
              tooltip={t('logview.setweight')}
              icon={<EditIcon fontSize="small" />}
            />
          )}
        </Stack>
      ),
      renderer: (x: Log) => {
        return (
          <Stack>
            {!x.start_weight && !x.end_weight && <div>N/A</div>}
            {!!x.start_weight && (
              <Stack justifyContent="space-between" direction="row">
                <span style={{ width: 45, display: 'inline-block' }}>{t('logview.start')}:</span>
                {weightValueToDisplay(x.start_weight)}
              </Stack>
            )}
            {!!x.end_weight && (
              <Stack justifyContent="space-between" direction="row">
                <span style={{ width: 45, display: 'inline-block' }}>{t('logview.end')}:</span>
                {weightValueToDisplay(x.end_weight)}
              </Stack>
            )}
            {!!x.start_weight && x.end_weight && (
              <Stack justifyContent="space-between" direction="row">
                <span style={{ width: 45, display: 'inline-block' }}>{t('logview.loss')}:</span>
                {calculateWeightLoss(x.start_weight, x.end_weight)}
              </Stack>
            )}
          </Stack>
        )
      },
    },
    {
      label: t('logview.color'),
      hovering_label: (
        <Stack direction="row" justifyContent="space-between">
          <Stack sx={{ py: 1 }}> {t('logview.color')} </Stack>
          {props.onSetColor && (
            <TooltipIconButton
              onClick={props.onSetColor}
              tooltip={t('logview.setcolor')}
              icon={<EditIcon fontSize="small" />}
            />
          )}
        </Stack>
      ),
      renderer: (x: Log) => {
        return (
          <Stack>
            {!!x.whole_bean_color && (
              <Stack justifyContent="space-between" direction="row">
                <span style={{ width: 45, display: 'inline-block' }}>{t('logview.wholebean')}:</span>
                {x.whole_bean_color}
              </Stack>
            )}
            {!!x.ground_bean_color && (
              <Stack justifyContent="space-between" direction="row">
                <span style={{ width: 45, display: 'inline-block' }}>{t('logview.groundbean')}:</span>
                {x.ground_bean_color}
              </Stack>
            )}
            {!!x.green_bean_color && (
              <Stack justifyContent="space-between" direction="row">
                <span style={{ width: 45, display: 'inline-block' }}>{t('logview.greenbean')}:</span>
                {x.green_bean_color}
              </Stack>
            )}
            {!!x.ground_bean_color && !!x.whole_bean_color && (
              <Tooltip title={t('logview.wholegroundexplanation')}>
                <Stack justifyContent="space-between" direction="row">
                  <span style={{ width: 45, display: 'inline-block' }}>{t('logview.grounwholecolordiffernce')}:</span>
                  {(x.whole_bean_color - x.ground_bean_color).toFixed(1)}
                </Stack>
              </Tooltip>
            )}
            {!!x.green_bean_color && !!x.whole_bean_color && (
              <Tooltip title={t('logview.wholegreenexplanation')}>
                <Stack justifyContent="space-between" direction="row">
                  <span style={{ width: 45, display: 'inline-block' }}>{t('logview.greenwholecolordiffernce')}:</span>
                  {(x.whole_bean_color - x.green_bean_color).toFixed(1)}
                </Stack>
              </Tooltip>
            )}
          </Stack>
        )
      },
    },
    {
      label: t('logsview.beanname'),
      hovering_label: (
        <Stack direction="row" justifyContent="space-between">
          <Stack sx={{ py: 1 }}>{t('logsview.beanname')}</Stack>
          {props.onSetInventory && (
            <TooltipIconButton
              onClick={props.onSetInventory}
              tooltip={t('logview.setbean')}
              icon={<EditIcon fontSize="small" />}
            />
          )}
        </Stack>
      ),
      renderer: (x: Log) => {
        return (
          <Stack direction="row" alignItems="flex-start">
            <InventoryLink log={x} />
          </Stack>
        )
      },
    },
    ...(props.machine?.sensor_config?.has_drum
      ? [
          {
            label: t('logsview.chargetdrumtemp'),
            hovering_label: undefined,
            renderer: (x: Log) => {
              const firstDrumtemp = props.logDatapoints[x.url].find((y) => y.msec === 0)?.drum_temp
              return firstDrumtemp ? getDisplayTemperature(firstDrumtemp) : 'N/A'
            },
          },
        ]
      : []),
    {
      label: t('logview.firstcrack'),
      renderer: (x: Log) => {
        return (
          <Stack>
            <div>{calculateTimeDiff(x.firstcrack_timestamp, x.start_timestamp)}</div>
            <div>{getDisplayTemperature(x.fc_temp) || 'N/A'}</div>
          </Stack>
        )
      },
    },
    {
      label: t('logview.devtime'),
      renderer: (x: Log) => {
        let devTimePercent = ''
        const endTimestamp = x.drop_timestamp || x.end_timestamp
        if (endTimestamp && x.start_timestamp && x.firstcrack_timestamp) {
          const totalTime = new Date(endTimestamp).getTime() - new Date(x.start_timestamp).getTime()
          const devTime = new Date(endTimestamp).getTime() - new Date(x.firstcrack_timestamp).getTime()
          devTimePercent = `${((devTime / totalTime) * 100).toFixed(2)}%`
        }
        return (
          <Stack>
            <div>{calculateTimeDiff(x.drop_timestamp || x.end_timestamp, x.firstcrack_timestamp)}</div>
            {devTimePercent && <div>{devTimePercent}</div>}
          </Stack>
        )
      },
    },
    {
      label: t('logview.end'),
      renderer: (x: Log) => {
        return (
          <Stack>
            <div>{calculateTimeDiff(x.drop_timestamp || x.end_timestamp, x.start_timestamp)}</div>
            <div>{getDisplayTemperature(x.end_temp) || 'N/A'}</div>
          </Stack>
        )
      },
    },
  ]

  const getCellClassName = (isFirst: boolean, isLast: boolean, isSelected: boolean): string => {
    const firstFragment = isFirst ? 'MuiTableCell-infotable-first' : ''
    const lastFragment = isLast ? 'MuiTableCell-infotable-last' : ''
    const isSelectedFragment = isSelected ? 'MuiTableCell-infotable-selected' : ''
    return `MuiTableCell-infotable ${isSelectedFragment} ${firstFragment} ${lastFragment}`
  }
  const renderAsTable = (): JSX.Element => {
    return (
      <TableContainer sx={{ mb: 4, overflow: 'hidden', minWidth: (props.logs.length + 1) * 120 }}>
        <Table>
          <TableBody>
            {tableRowConfig.map((configValue, i) => {
              return (
                <TableRow key={configValue.label}>
                  <TableCell
                    variant="head"
                    className={getCellClassName(false, false, false)}
                    sx={{ color: 'text.secondary', backgroundColor: 'background.default' }}
                  >
                    {configValue.label}
                  </TableCell>
                  {props.logs.map((log) => {
                    return (
                      <TableCell
                        key={log.id}
                        sx={{ position: 'relative' }}
                        className={getCellClassName(
                          i === 0,
                          i === tableRowConfig.length - 1,
                          props.selectedLogId === log.id
                        )}
                        onClick={(): void => {
                          if (props.onSelectedLog) {
                            const selectedLogId = log.id === props.selectedLogId ? 0 : log.id
                            props.onSelectedLog(selectedLogId)
                          }
                        }}
                      >
                        <>
                          {configValue.renderer(log)}
                          {i === 0 && props.logs.length > 1 && (
                            <Stack sx={{ position: 'absolute', top: '-10px', right: '-7px', opacity: 0.5 }}>
                              <TooltipIconButton
                                onClick={(event) => {
                                  event.stopPropagation()
                                  if (props.onDeselectLog) {
                                    props.onDeselectLog(log.id)
                                  }
                                  if (props.onSelectedLog && props.selectedLogId === log.id) {
                                    props.onSelectedLog(0)
                                  }
                                }}
                                tooltip={t('logsview.deselectforcompare')}
                                icon={<CloseIcon />}
                              />
                            </Stack>
                          )}
                        </>
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  const renderAsInfoFields = (): JSX.Element => {
    return (
      <Stack sx={{ overflow: 'auto' }}>
        {props.logs.map((log) => {
          return (
            <Stack key={log.id} spacing={2}>
              {tableRowConfig.map((configValue, i) => (
                <Stack key={configValue.label}>
                  <InfoElement
                    label={configValue.hovering_label || configValue.label}
                    value={configValue.renderer(log)}
                  />
                  {(i === 0 || i === 2 || i === 5) && <Divider sx={{ pt: 2 }} />}
                </Stack>
              ))}
              {props.children}
            </Stack>
          )
        })}
      </Stack>
    )
  }

  return props.onSelectedLog ? renderAsTable() : renderAsInfoFields()
}

export default LogInfoTable
